import { Column } from "primereact/column";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from "primereact/datatable";
import React, { useEffect, useState } from "react";
import { MdArrowDropDown, MdArrowRight } from "react-icons/md";
import { GLOBAL_DEFAULT } from "../../../constants";
import CustomPaginator from "./CustomPaginator";
import "./datagrid.scss";

export interface IDataGridCommon {
  columns: any;
  data: any;
  onPageChange?: any;
  onSort?: any;
  currentPage?: any;
  tableRecords?: any;
  HideTablePaginator?: boolean;
  reorderableRows?: any;
  selectedProducts?: any;
  onSelectionChange?: any;
  onRowReorder?: any;
  selectionMode?: any;
  globalFilterFields?: any;
  filterDisplay?: any;
  emptyMessage?: any;
  globalFilterMatchMode?: any;
  isRowSelectable?: any;
  columnsExpended?: any;
  rowPerPage?: any;
  sortingOrder?: any;
  sortField?: any;
  dataKey?: string;
  showExpendedRows?: boolean;
  onRowClick?: any;
  rowClassName?: any;
  dataTableClassName?: any;
  showHeaders?: boolean;
  stripedRows?: boolean;
}
interface ColumnInfo {
  field: string;
  order: number;
}

/**
 * Custom component for Data Grid Chekbox used at all places
 *
 * @param {{ columns: any; data: any; onSort: any; onPageChange: any; tableRecords: any; HideTablePaginator: any; reorderableRows: any; selectedProducts: any; onSelectionChange: any; onRowReorder: any; selectionMode: any; ... 16 more ...; stripedRows: any; }} param0
 * @param {*} param0.columns
 * @param {*} param0.data
 * @param {*} param0.onSort
 * @param {*} param0.onPageChange
 * @param {*} param0.tableRecords
 * @param {*} param0.HideTablePaginator
 * @param {*} param0.reorderableRows
 * @param {*} param0.selectedProducts
 * @param {*} param0.onSelectionChange
 * @param {*} param0.onRowReorder
 * @param {*} param0.selectionMode
 * @param {*} param0.globalFilterFields
 * @param {*} param0.filterDisplay
 * @param {*} param0.emptyMessage
 * @param {*} param0.globalFilterMatchMode
 * @param {*} param0.isRowSelectable
 * @param {*} param0.columnsExpended
 * @param {*} param0.currentPage
 * @param {*} param0.rowPerPage
 * @param {*} param0.sortingOrder
 * @param {*} param0.sortField
 * @param {*} param0.dataKey
 * @param {*} param0.showExpendedRows
 * @param {*} param0.onRowClick
 * @param {*} param0.rowClassName
 * @param {*} param0.dataTableClassName
 * @param {*} param0.showHeaders
 * @param {*} param0.stripedRows
 * @returns
 */

const DataGridCommon: React.FC<IDataGridCommon> = ({
  columns,
  data,
  onSort,
  onPageChange,
  tableRecords,
  HideTablePaginator,
  reorderableRows,
  selectedProducts,
  onSelectionChange,
  onRowReorder,
  selectionMode,
  globalFilterFields,
  filterDisplay,
  emptyMessage,
  globalFilterMatchMode,
  isRowSelectable,
  columnsExpended,
  currentPage,
  rowPerPage,
  sortingOrder,
  sortField,
  dataKey,
  showExpendedRows,
  onRowClick,
  rowClassName,
  dataTableClassName,
  showHeaders,
  stripedRows,
}: IDataGridCommon) => {
  const sortData: any = [{ field: sortField, order: 1 }];
  const [sortOrder, setSortOrder] = useState<any>(
    sortingOrder || GLOBAL_DEFAULT.SORT_ORDER,
  );
  const [multiSortMeta, setMultiSortMeta] = useState<[]>(sortData || []);
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  useEffect(() => {
    if (sortOrder && sortField) {
      const multiSort: any = [
        {
          field: sortField,
          order: sortOrder === GLOBAL_DEFAULT.SORT_ORDER ? -1 : 1,
        },
      ];
      setMultiSortMeta(multiSort);
      setSortOrder(sortOrder);
      onSort(sortOrder, sortField);
    }

    if (showExpendedRows) {
      expandAll();
    }
  }, [data]);

  const expandAll = () => {
    const _expandedRows: any = {};

    data?.forEach((p: any) => {
      if (p.product_type === "Bundle") {
        _expandedRows[`${p?.[dataKey ?? "id"]}`] = true;
      }
    });

    setExpandedRows(_expandedRows);
  };

  const handlePageChange = (newPage: number, rows: any) => {
    onPageChange(newPage, rows); // Call the prop function to update currentPage in the parent component
  };
  //Sort List using header
  const handleSort = (event: any) => {
    setMultiSortMeta(event.multiSortMeta);

    event.multiSortMeta.forEach((columnInfo: ColumnInfo) => {
      const { field, order } = columnInfo;
      const sortOrder = order !== 1 ? "DESC" : "ASC";
      setSortOrder(sortOrder);
      // Call onSort with the updated sortOrder and sortField values
      onSort(sortOrder, field);
    });
  };

  const rowExpansionTemplate = (data: any) => {
    return (
      <DataTable
        className="expendedDataTable"
        value={data?.items}
        showHeaders={false}
        emptyMessage={emptyMessage ?? "No Core Product available"}
        lazy={true}
        stripedRows={stripedRows ?? true}
      >
        {columnsExpended?.map((col: any, index: number) => (
          <Column
            key={`data-col-${col?.field}-${index}`}
            field={col.field}
            style={{
              width: col.width,
              maxWidth: col.width,
              minWidth: col.width,
            }}
            body={col.body}
            bodyClassName={col.bodyClassName}
          />
        ))}
      </DataTable>
    );
  };

  return (
    <div className="d-flex dataGridMain flex-column h-100 overflow-auto w-100">
      <DataTable
        dataKey={dataKey ?? "id"}
        value={data}
        stripedRows={stripedRows ?? false}
        onSort={handleSort}
        sortMode="multiple"
        multiSortMeta={multiSortMeta}
        // sortIcon={
        //   sortOrder === "DESC" ? (
        //     <FiArrowDown className="p-sortable-column-icon" size={17} />
        //   ) : (
        //     <FiArrowUp className="p-sortable-column-icon" size={17} />
        //   )
        // }
        className={`flex-grow-1 h-auto overflow-auto ${dataTableClassName ?? ""}`}
        scrollable={true}
        scrollHeight="flex"
        reorderableRows={reorderableRows}
        onRowReorder={onRowReorder}
        selection={selectedProducts}
        onSelectionChange={onSelectionChange}
        selectionMode={selectionMode}
        globalFilterFields={globalFilterFields}
        filterDisplay={filterDisplay}
        emptyMessage={emptyMessage ?? "No records available"}
        globalFilterMatchMode={globalFilterMatchMode}
        isDataSelectable={isRowSelectable}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        collapsedRowIcon={
          <MdArrowRight size={24} className="text-body text-red" />
        }
        expandedRowIcon={<MdArrowDropDown size={24} className="text-body" />}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
        lazy={true}
        showHeaders={showHeaders}
      >
        {columns.map((col: any, index: number) => (
          <Column
            key={`data-col-${col?.field}-${index}`}
            sortable={col.sortable}
            field={col.field}
            header={col.header}
            headerClassName={`fs-15 ${col.headerClassName}`}
            style={{
              width: col.width,
              maxWidth: col.width,
              minWidth: col.width,
            }}
            body={col.body}
            bodyClassName={col.bodyClassName}
            rowReorder={col.rowReorder}
            selectionMode={col.selectionMode}
            filter={col.filter}
            filterMatchModeOptions={[{ label: "Contains", value: "contains" }]}
            showFilterMatchModes={false}
            filterMatchMode={col.filterMatchMode}
            expander={col.expander}
            colSpan={col.colSpan}
          />
        ))}
      </DataTable>

      {/* Custom Pagination */}
      {HideTablePaginator ? (
        ""
      ) : (
        <CustomPaginator
          data={data}
          onPageChange={handlePageChange}
          tableRecords={tableRecords}
          currentPage={currentPage}
          rowPerPage={rowPerPage}
        />
      )}
    </div>
  );
};

export default DataGridCommon;
